import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';

import { getFeatureLabel } from './feature-labels';

const GENERAL_SECTION = '__general__';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const setIn = (object, path, value) => {
  if (path.length === 0) return value;

  const [key, ...rest] = path;

  return { ...object, [key]: setIn(object?.[key], rest, value) };
};

const countEnabled = (value) => {
  if (value === true) return 1;
  if (!isPlainObject(value)) return 0;

  return Object.values(value).reduce(
    (sum, child) => sum + countEnabled(child),
    0
  );
};

const Checkbox = ({ checked, onChange, label, isMain }) => (
  <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-py-1">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="tw-size-4 tw-cursor-pointer tw-accent-main"
    />
    <span
      className={`tw-text-[13px] ${
        isMain
          ? 'tw-font-medium tw-text-text-dark-high-importance'
          : 'tw-text-black'
      }`}
    >
      {label}
    </span>
  </label>
);

const InlineField = ({ label, children }) => (
  <label className="tw-flex tw-items-center tw-gap-3 tw-py-1">
    <span className="tw-w-48 tw-shrink-0 tw-text-[13px] tw-text-black">
      {label}
    </span>
    {children}
  </label>
);

const inputClassName =
  'tw-h-8 tw-w-full tw-max-w-[420px] tw-rounded-[5px] tw-border tw-border-solid tw-border-input-border tw-bg-white tw-px-2.5 tw-text-[13px] focus:tw-border-input-borderFocused focus:tw-outline-none';

/**
 * Rend une valeur de configuration : case à cocher pour un booléen, champ
 * pour un texte / nombre, et récursion pour un objet. Un objet porteur d'un
 * `enabled` est affiché comme une option dont les réglages ne s'ouvrent
 * qu'une fois cochée.
 */
const FeatureNode = ({ name, value, path, onChange }) => {
  const label = getFeatureLabel(name);
  const update = (nextValue) => onChange(path, nextValue);

  if (typeof value === 'boolean') {
    return <Checkbox checked={value} onChange={update} label={label} />;
  }

  if (typeof value === 'string') {
    return (
      <InlineField label={label}>
        <input
          className={inputClassName}
          value={value}
          onChange={(event) => update(event.target.value)}
        />
      </InlineField>
    );
  }

  if (typeof value === 'number') {
    return (
      <InlineField label={label}>
        <input
          type="number"
          className={`${inputClassName} tw-max-w-[120px]`}
          value={value}
          onChange={(event) => update(Number(event.target.value))}
        />
      </InlineField>
    );
  }

  if (Array.isArray(value)) {
    const isStringList = value.every((item) => typeof item === 'string');

    return (
      <InlineField label={label}>
        {isStringList ? (
          <input
            className={inputClassName}
            value={value.join(', ')}
            placeholder="Valeurs séparées par des virgules"
            onChange={(event) =>
              update(
                event.target.value
                  .split(',')
                  .map((item) => item.trim())
                  .filter(Boolean)
              )
            }
          />
        ) : (
          <span className="tw-text-[11px] tw-text-[#76808c]">
            {value.length} élément(s) · modifiable dans l’éditeur JSON
          </span>
        )}
      </InlineField>
    );
  }

  if (!isPlainObject(value)) return null;

  const hasToggle = typeof value.enabled === 'boolean';
  const childKeys = Object.keys(value).filter(
    (key) => !(hasToggle && key === 'enabled')
  );
  const isOpen = !hasToggle || value.enabled;

  return (
    <div>
      {hasToggle ? (
        <Checkbox
          checked={value.enabled}
          onChange={(checked) => onChange([...path, 'enabled'], checked)}
          label={label}
          isMain
        />
      ) : (
        <div className="tw-pb-0.5 tw-pt-1.5 tw-text-[13px] tw-font-medium tw-text-text-dark-high-importance">
          {label}
        </div>
      )}
      {isOpen && childKeys.length > 0 && (
        <div className="tw-ml-[7px] tw-border-0 tw-border-l tw-border-solid tw-border-[#eeeff2] tw-pl-4">
          {childKeys.map((key) => (
            <FeatureNode
              key={key}
              name={key}
              value={value[key]}
              path={[...path, key]}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FeatureSection = ({ title, enabledCount, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] last:tw-border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-0 tw-bg-white tw-px-4 tw-py-3 tw-text-left hover:tw-bg-[rgba(118,128,140,0.04)]"
      >
        <FontAwesomeIcon
          icon={isOpen ? faChevronDown : faChevronRight}
          className="tw-size-3 tw-text-[#76808c]"
        />
        <span className="tw-flex-1 tw-text-[13px] tw-font-semibold tw-text-text-dark-high-importance">
          {title}
        </span>
        <span className="tw-rounded-full tw-bg-[rgba(7,153,146,0.08)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-main-active">
          {enabledCount} activée{enabledCount > 1 ? 's' : ''}
        </span>
      </button>
      {isOpen && <div className="tw-px-4 tw-pb-3 tw-pl-10">{children}</div>}
    </div>
  );
};

export const FeatureTree = ({ value, onChange }) => {
  // Les clés de premier niveau qui ne sont pas des objets (cas de features v1)
  // sont regroupées dans une section « Général ».
  const sections = useMemo(() => {
    const entries = Object.entries(value || {});
    const general = entries.filter(([, child]) => !isPlainObject(child));
    const objects = entries.filter(([, child]) => isPlainObject(child));

    return [
      ...(general.length > 0
        ? [{ key: GENERAL_SECTION, title: 'Général', entries: general }]
        : []),
      ...objects.map(([key, child]) =>
        typeof child.enabled === 'boolean'
          ? { key, title: getFeatureLabel(key), entries: [[key, child]] }
          : {
              key,
              title: getFeatureLabel(key),
              entries: Object.entries(child),
              path: [key],
            }
      ),
    ];
  }, [value]);

  const handleChange = (path, nextValue) =>
    onChange(setIn(value, path, nextValue));

  return (
    <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
      {sections.map((section) => (
        <FeatureSection
          key={section.key}
          title={section.title}
          enabledCount={section.entries.reduce(
            (sum, [, child]) => sum + countEnabled(child),
            0
          )}
        >
          {section.entries.map(([key, child]) => (
            <FeatureNode
              key={key}
              name={key}
              value={child}
              path={[...(section.path || []), key]}
              onChange={handleChange}
            />
          ))}
        </FeatureSection>
      ))}
    </div>
  );
};

export const JsonEditor = ({ value, onChange }) => {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState(null);
  const [emittedValue, setEmittedValue] = useState(value);

  // Resynchronise le texte quand la valeur change hors de l'éditeur (copie
  // d'un client, valeurs par défaut). Une valeur émise par la saisie elle-même
  // est ignorée, sinon le texte serait reformaté à chaque frappe.
  if (value !== emittedValue) {
    setEmittedValue(value);
    setText(JSON.stringify(value, null, 2));
    setError(null);
  }

  const handleChange = (nextText) => {
    setText(nextText);

    try {
      const parsed = JSON.parse(nextText);

      if (!isPlainObject(parsed)) {
        setError('La configuration doit être un objet JSON.');
        return;
      }

      setError(null);
      setEmittedValue(parsed);
      onChange(parsed);
    } catch (parseError) {
      setError(`JSON invalide : ${parseError.message}`);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <textarea
        value={text}
        onChange={(event) => handleChange(event.target.value)}
        spellCheck={false}
        className={`tw-h-[420px] tw-w-full tw-resize-y tw-rounded-[5px] tw-border tw-border-solid tw-bg-white tw-p-3 tw-font-mono tw-text-[12px] focus:tw-outline-none ${
          error ? 'tw-border-input-required' : 'tw-border-input-border'
        }`}
      />
      {error && (
        <div className="tw-text-[11px] tw-text-input-required">{error}</div>
      )}
    </div>
  );
};
